import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDailyAnalytics } from '../../store/slices/taskSlice';
import { fetchDayActivityTimeline } from '../../services/attendanceService';
import { calcOfficeProductivityPercent } from '../attendance/WorkSessionWatch';

let lastFetchedDate = '';

const ProductivityDashboard = () => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [officeMins, setOfficeMins] = useState(0);
  const { dailyAnalytics, loading } = useSelector((state) => state.tasks);

  useEffect(() => {
    if (selectedDate && lastFetchedDate !== selectedDate) {
      lastFetchedDate = selectedDate;
      dispatch(fetchDailyAnalytics(selectedDate));
    }
  }, [dispatch, selectedDate]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchDayActivityTimeline(selectedDate);
        if (cancelled) return;
        const working = Number(data?.workingTimeMinutes);
        if (Number.isFinite(working) && working >= 0) {
          setOfficeMins(Math.floor(working));
          return;
        }
        const total = Number(data?.totalTimeMinutes) || 0;
        const breaks = Number(data?.totalBreakMinutes) || 0;
        setOfficeMins(Math.max(0, Math.floor(total - breaks)));
      } catch {
        if (!cancelled) setOfficeMins(0);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  const {
    totalPickedTasks = 0,
    totalPickedMinutes = 0,
    totalCompletedTasks = 0,
    totalCompletedMinutes = 0,
    totalPlannedReminders = 0,
    totalPlannedReminderMinutes = 0,
    totalCompletedReminders = 0,
    totalCompletedReminderMinutes = 0,
    totalCompletedWorkflows = 0,
    totalCompletedWorkflowMinutes = 0,
    totalPlannedMinutes,
    totalCompletedMinutesCombined,
    pickedTasks = [],
    completedTasks = [],
  } = dailyAnalytics || {};

  const plannedMins =
    totalPlannedMinutes ?? totalPickedMinutes + (totalPlannedReminderMinutes || 0) + (totalCompletedWorkflowMinutes || 0);
  const doneMins =
    totalCompletedMinutesCombined ??
    totalCompletedMinutes + (totalCompletedReminderMinutes || 0) + (totalCompletedWorkflowMinutes || 0);
  const plannedCount = totalPickedTasks + (totalPlannedReminders || 0) + (totalCompletedWorkflows || 0);
  const completedCount = totalCompletedTasks + (totalCompletedReminders || 0) + (totalCompletedWorkflows || 0);
  const productivityPercentage = calcOfficeProductivityPercent(doneMins, officeMins);

  return (
    <div className="p-6 max-w-5xl mx-auto w-full">
      <div className="flex justify-between items-center mb-6 border-b border-gray-200 pb-4 dark:border-gray-700">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Productivity Dashboard</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Track tasks, reminders, and workflows planned vs completed
          </p>
        </div>
        <div className="flex items-center gap-3">
          {loading && <span className="text-sm text-gray-400">Loading...</span>}
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-3 py-2 border outline-none"
          />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Planned</p>
          <p className="text-3xl font-semibold text-gray-900 dark:text-white mt-2">{plannedCount}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {totalPickedTasks} tasks · {totalPlannedReminders} reminders
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Completed</p>
          <p className="text-3xl font-semibold text-gray-900 dark:text-white mt-2">{completedCount}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {totalCompletedTasks} tasks · {totalCompletedReminders} reminders
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Est. Mins Planned</p>
          <p className="text-3xl font-semibold text-indigo-600 dark:text-indigo-400 mt-2">{plannedMins}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {totalPickedMinutes} tasks · {totalPlannedReminderMinutes} reminders · {totalCompletedWorkflowMinutes} workflows
          </p>
        </div>
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col justify-between">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Est. Mins Completed</p>
          <p className="text-3xl font-semibold text-green-600 dark:text-green-400 mt-2">{doneMins}</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {totalCompletedMinutes} tasks · {totalCompletedReminderMinutes} reminders · {totalCompletedWorkflowMinutes} workflows
          </p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Productivity Score</h3>
          <span className="text-3xl font-bold text-primary-600 dark:text-primary-400">{productivityPercentage}%</span>
        </div>
        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4 mb-3">
          <div
            className="bg-primary-500 h-4 rounded-full transition-all duration-700 ease-out"
            style={{ width: `${Math.min(100, productivityPercentage)}%` }}
          ></div>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
          {doneMins} completed work mins / {officeMins} office mins
          {officeMins > 0 ? ` (${productivityPercentage}%)` : ''}
        </p>
      </div>

      {/* Lists */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="flex flex-col h-full">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-indigo-500"></span>
            Tasks Picked ({pickedTasks.length})
          </h3>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex-1 overflow-hidden flex flex-col">
            {pickedTasks.length === 0 ? (
              <div className="p-8 text-sm text-gray-500 flex-1 flex items-center justify-center">No tasks picked on this date</div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 overflow-y-auto max-h-96">
                {pickedTasks.map((task) => (
                  <li key={`picked-${task.id}`} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={task.title || task.description?.replace(/<[^>]*>?/gm, '')}>
                      {task.title || (task.description ? task.description.replace(/<[^>]*>?/gm, '').substring(0, 40) + '...' : 'Untitled Task')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">Est: {task.estimateTimeInMinutes || 0} mins</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="flex flex-col h-full">
          <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-green-500"></span>
            Tasks Completed ({completedTasks.length})
          </h3>
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 flex-1 overflow-hidden flex flex-col">
            {completedTasks.length === 0 ? (
              <div className="p-8 text-sm text-gray-500 flex-1 flex items-center justify-center">No tasks completed on this date</div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 overflow-y-auto max-h-96">
                {completedTasks.map((task) => (
                  <li key={`completed-${task.id}`} className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate" title={task.title || task.description?.replace(/<[^>]*>?/gm, '')}>
                      {task.title || (task.description ? task.description.replace(/<[^>]*>?/gm, '').substring(0, 40) + '...' : 'Untitled Task')}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-medium">Est: {task.estimateTimeInMinutes || 0} mins</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductivityDashboard;
